"use client";

import { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { createArea, deleteArea, updateArea, deleteAllAreas } from "../../api/area";
import customFetch from "hooks/fetchWrapper";
import ConfirmationDialog from "hooks/ConfirmationDialog";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import * as XLSX from "xlsx";

const AreaTab = () => {
  const [rows, setRows] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    aid: null,
    code: "",
    eid: "",
    areaname: "",
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canConfirm, setCanConfirm] = useState(false);
  const countdownRef = useRef(null);

  // Excel menu state
  const [excelMenuAnchorEl, setExcelMenuAnchorEl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchAreas();
    fetchEmirates();
  }, []);

  useEffect(() => {
    if (openDeleteAllDialog && countdown > 0) {
      countdownRef.current = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCanConfirm(true);
      clearTimeout(countdownRef.current);
    }

    return () => clearTimeout(countdownRef.current);
  }, [openDeleteAllDialog, countdown]);

  const handleCloseDeleteAll = () => {
    setOpenDeleteAllDialog(false);
    setCountdown(5);
    setCanConfirm(false);
    clearTimeout(countdownRef.current);
  };

  const handleConfirmDeleteAll = async () => {
    try {
      const response = await deleteAllAreas();
      if (response.status) {
        setRows([]); // Clear the table
        alert("All areas deleted successfully!");
      } else {
        alert("Failed to delete all areas: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Delete all areas failed:", error);
      alert("Operation failed");
    } finally {
      handleCloseDeleteAll();
    }
  };

  const fetchAreas = async () => {
    try {
      const data = await customFetch("areas");
      if (data.status && Array.isArray(data.data)) {
        setRows(data.data);
      }
    } catch (error) {
      console.error("Error fetching areas:", error);
    }
  };

  const fetchEmirates = async () => {
    try {
      const data = await customFetch("emirates");
      setEmirates(data);
    } catch (error) {
      console.error("Error fetching emirates:", error);
    }
  };

  const handleOpenAddModal = () => {
    setIsEditMode(false);
    setFormData({ aid: null, code: "", eid: "", areaname: "" });
    setOpenModal(true);
  };

  const handleOpenEditModal = (area) => {
    setIsEditMode(true);
    setFormData({
      aid: area.aid,
      code: area.code || "",
      eid: area.eid || "",
      areaname: area.areaname || "",
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({ aid: null, code: "", eid: "", areaname: "" });
  };

  const handleSubmit = async () => {
    if (!formData.code || !formData.eid || !formData.areaname) {
      alert("Please fill all required fields");
      return;
    }

    const payload = [
      {
        ...(isEditMode && { aid: formData.aid }),
        code: formData.code,
        eid: formData.eid,
        areaname: formData.areaname,
      },
    ];

    try {
      let response;
      if (isEditMode) {
        response = await updateArea(formData.aid, payload[0]); // Assuming updateArea accepts single object or id + data
      } else {
        response = await createArea(payload);
      }

      if (response.status) {
        fetchAreas(); // Refresh list
        handleCloseModal();
        alert(isEditMode ? "Area updated successfully!" : "Area added successfully!");
      }
    } catch (error) {
      console.error("Error saving area:", error);
      alert("Failed to save area");
    }
  };

  const handleDelete = (id) => () => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteArea(deleteId);
      setRows((prev) => prev.filter((r) => r.aid !== deleteId));
    } catch (error) {
      console.error("Error deleting area:", error);
    }
    setOpenConfirmDialog(false);
  };

  // Excel handlers (unchanged)
  const handleExcelButtonClick = (event) => {
    setExcelMenuAnchorEl(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchorEl(null);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const wsData = [["code", "emirate", "area_name"]];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Areas");
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const fileName = `areas_template_${dateStr}_${timeStr}.xlsx`;
    XLSX.writeFile(wb, fileName);
    handleExcelMenuClose();
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
    handleExcelMenuClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        await bulkCreateAreas(data);
      }
    } catch (error) {
      console.error("Error processing Excel file:", error);
    }
    event.target.value = null;
  };

  const readExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const workbook = XLSX.read(e.target.result, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          const validData = jsonData.filter((item) => {
            const isValid =
              (item.code && item.emirate && item.area_name && typeof item.code === "string") ||
              typeof item.code === "number";
            if (isValid) {
              item.code = String(item.code);
              item.eid = String(item.emirate).toUpperCase();
              item.areaname = String(item.area_name);
            }
            return isValid;
          });
          resolve(validData);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const bulkCreateAreas = async (areasData) => {
    try {
      const payload = areasData.map((area) => ({
        code: area.code,
        eid: area.eid,
        areaname: area.areaname,
      }));
      const response = await createArea(payload);
      if (response.status && response.data) {
        const responseData = Array.isArray(response.data) ? response.data : [response.data];
        const newAreas = responseData.map((areaData) => ({
          ...areaData,
          emirateName: emirates.find((e) => e.eid === areaData.eid)?.name,
        }));
        setRows((prev) => [...prev, ...newAreas]);
      }
    } catch (error) {
      console.error("Error bulk creating areas:", error);
    }
  };

  const columns = [
    { field: "code", headerName: "Code", flex: 0.8, minWidth: 100 },
    { field: "areaname", headerName: "Area Name", flex: 2, minWidth: 180 },
    { field: "emirateName", headerName: "Emirate", flex: 1.5, minWidth: 140 },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 0.8,
      minWidth: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenEditModal(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDelete(params.id)}
        />,
      ],
    },
  ];

  return (
    <Box sx={{ height: 500, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setOpenDeleteAllDialog(true);
            setCountdown(5);
            setCanConfirm(false);
          }}
        >
          Delete All Areas
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleExcelButtonClick}
          startIcon={<BackupTableIcon />}
        >
          Excel
        </Button>
        <Menu
          anchorEl={excelMenuAnchorEl}
          open={Boolean(excelMenuAnchorEl)}
          onClose={handleExcelMenuClose}
        >
          <MenuItem onClick={handleDownloadTemplate}>
            <ListItemIcon>
              <FileDownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download Template</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleUploadClick}>
            <ListItemIcon>
              <FileUploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload File</ListItemText>
          </MenuItem>
        </Menu>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".xlsx,.xls"
          style={{ display: "none" }}
        />
        <Button variant="contained" onClick={handleOpenAddModal}>
          Add New Area
        </Button>
      </Box>

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.aid}
        pageSizeOptions={[10, 25, 50]}
      />

      {/* Modal for Add/Edit */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <h2>{isEditMode ? "Edit Area" : "Add New Area"}</h2>

          <TextField
            autoFocus
            margin="dense"
            label="Area Code"
            fullWidth
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Area Name"
            fullWidth
            value={formData.areaname}
            onChange={(e) => setFormData({ ...formData, areaname: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="emirate-select-label">Emirate</InputLabel>
            <Select
              labelId="emirate-select-label"
              value={formData.eid}
              label="Emirate"
              onChange={(e) => setFormData({ ...formData, eid: e.target.value })}
            >
              {emirates?.map((emirate) => (
                <MenuItem key={emirate.eid} value={emirate.eid}>
                  {emirate.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal open={openDeleteAllDialog} onClose={handleCloseDeleteAll}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 }, // Same responsive width as your example
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Delete ALL Areas?
          </Typography>
          <Typography variant="body1" sx={{ my: 3 }}>
            This will permanently delete all areas and cannot be undone.
          </Typography>
          <Typography variant="h4" sx={{ my: 4, fontWeight: "bold" }}>
            {countdown}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {canConfirm
              ? "You can now confirm deletion"
              : "Please wait for the countdown to finish"}
          </Typography>

          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
            <Button onClick={handleCloseDeleteAll} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteAll}
              disabled={!canConfirm}
            >
              Yes, Delete All
            </Button>
          </Box>
        </Box>
      </Modal>

      <ConfirmationDialog
        open={openConfirmDialog}
        title="Confirm Deletion"
        description="Are you sure you want to delete this area? This action cannot be undone."
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmDelete}
      />
    </Box>
  );
};

export default AreaTab;
